/**
 * Behavior for the pdfHeader section.
 *
 * - Renders the first page of each ACF-attached PDF directly to a <canvas>
 *   via PDF.js (loaded from CDN). Editors only need to upload one PDF per
 *   item — no companion PNG required.
 * - Clicking a thumbnail (or the next/prev carousel arrows) swaps the main
 *   canvas, the Print target, and the Download link to that PDF.
 * - Print uses a hidden iframe to fire `print()` directly on the PDF without
 *   opening the browser's PDF viewer in a new tab. Falls back to opening the
 *   PDF in a new window in browsers that block iframe.print() on PDFs.
 */

const PDFJS_VERSION = '4.7.76'
const PDFJS_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build`

type PdfDocument = {
  getPage(num: number): Promise<PdfPage>
}

type PdfPage = {
  getViewport(opts: { scale: number }): { width: number; height: number }
  render(opts: {
    canvasContext: CanvasRenderingContext2D
    viewport: { width: number; height: number }
  }): { promise: Promise<void> }
}

type PdfJsLib = {
  getDocument(src: string): { promise: Promise<PdfDocument> }
  GlobalWorkerOptions: { workerSrc: string }
}

let pdfjsPromise: Promise<PdfJsLib> | null = null
const renderedCanvases = new WeakSet<HTMLCanvasElement>()
const previewRefreshTimers = new WeakMap<HTMLElement, number>()

// Must match the preview's CSS transition duration in pdf-header.scss so the
// canvas re-renders just after the box settles at its new size.
const PREVIEW_TRANSITION_MS = 350

function schedulePreviewRefresh(
  preview: HTMLElement,
  canvas: HTMLCanvasElement,
  url: string,
) {
  const existing = previewRefreshTimers.get(preview)
  if (existing !== undefined) window.clearTimeout(existing)
  const timer = window.setTimeout(() => {
    previewRefreshTimers.delete(preview)
    void renderPdfTo(canvas, url, { isRefresh: true })
  }, PREVIEW_TRANSITION_MS + 50)
  previewRefreshTimers.set(preview, timer)
}

function loadPdfJs(): Promise<PdfJsLib> {
  if (pdfjsPromise) return pdfjsPromise
  pdfjsPromise = import(/* @vite-ignore */ `${PDFJS_BASE}/pdf.min.mjs`).then(
    (mod: { default?: PdfJsLib } & PdfJsLib) => {
      const lib = (mod.default ?? mod) as PdfJsLib
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.mjs`
      return lib
    },
  )
  return pdfjsPromise
}

async function renderPdfTo(
  canvas: HTMLCanvasElement,
  url: string,
  opts: { isRefresh?: boolean } = {},
) {
  if (!url) return
  const lib = await loadPdfJs()
  const doc = await lib.getDocument(url).promise
  const page = await doc.getPage(1)

  const baseViewport = page.getViewport({ scale: 1 })

  // Publish each PDF's native aspect onto its surrounding box (thumb tile or
  // main preview) so CSS sizes the frame to the page's perspective. The
  // preview transitions, so a deferred re-render captures the crisp final
  // size — see schedulePreviewRefresh.
  const aspectValue = `${baseViewport.width} / ${baseViewport.height}`
  const thumb = canvas.closest<HTMLElement>('.pdf-header__thumb')
  thumb?.style.setProperty('--pdf-aspect', aspectValue)

  if (canvas.classList.contains('pdf-header__preview__canvas')) {
    const section = canvas.closest<HTMLElement>('[data-pdf-header]')
    const preview = canvas.closest<HTMLElement>('.pdf-header__preview')
    if (section) {
      section.dataset.pdfOrientation =
        baseViewport.width > baseViewport.height ? 'landscape' : 'portrait'
    }
    if (preview) {
      preview.style.setProperty('--pdf-aspect', aspectValue)
      if (renderedCanvases.has(canvas) && !opts.isRefresh) {
        schedulePreviewRefresh(preview, canvas, url)
      }
    }
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  // Fit to the canvas's CSS box without upsampling beyond its natural size.
  const cssWidth = canvas.clientWidth || 600
  const scale = (cssWidth / baseViewport.width) * dpr
  const viewport = page.getViewport({ scale })

  // Render to an offscreen canvas first; assigning to the visible canvas's
  // width/height clears its pixels, so drawing the finished bitmap in one
  // synchronous step avoids a blank flash while the async render completes.
  const offscreen = document.createElement('canvas')
  offscreen.width = Math.ceil(viewport.width)
  offscreen.height = Math.ceil(viewport.height)
  const offCtx = offscreen.getContext('2d')
  if (!offCtx) return
  await page.render({ canvasContext: offCtx, viewport }).promise

  canvas.width = offscreen.width
  canvas.height = offscreen.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.drawImage(offscreen, 0, 0)
  renderedCanvases.add(canvas)
}

function bindShare(scope: ParentNode) {
  scope.querySelectorAll<HTMLButtonElement>('[data-pdf-share-toggle]').forEach((toggle) => {
    if (toggle.dataset.pdfShareBound) return
    toggle.dataset.pdfShareBound = 'true'

    toggle.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      const root = toggle.closest('[data-pdf-share]')
      const menu = root?.querySelector<HTMLElement>('[data-pdf-share-menu]')
      if (!menu) return

      const open = menu.hasAttribute('data-open')
      closeAllShareMenus()
      if (!open) {
        menu.setAttribute('data-open', '')
        menu.hidden = false
        toggle.setAttribute('aria-expanded', 'true')
      }
    })
  })

  // The app router (IS_LINKS_ENABLED = false) intercepts all non-_blank anchor
  // clicks. mailto: links need explicit handling so the email client opens.
  scope.querySelectorAll<HTMLAnchorElement>('[data-pdf-share-link="email"]').forEach((link) => {
    if (link.dataset.pdfEmailBound) return
    link.dataset.pdfEmailBound = 'true'

    link.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      window.location.href = link.href
    })
  })
}

function closeAllShareMenus() {
  document.querySelectorAll<HTMLElement>('[data-pdf-share-menu]').forEach((menu) => {
    menu.removeAttribute('data-open')
    menu.hidden = true
    const toggle = menu.closest('[data-pdf-share]')?.querySelector('[data-pdf-share-toggle]')
    toggle?.setAttribute('aria-expanded', 'false')
  })
}

function setActivePdf(section: HTMLElement, url: string, filename: string) {
  if (!url) return
  section.dataset.pdfActiveUrl = url
  section.dataset.pdfActiveFilename = filename

  const main = section.querySelector<HTMLCanvasElement>('.pdf-header__preview__canvas')
  if (main) {
    main.dataset.pdfUrl = url
    void renderPdfTo(main, url)
  }

  const download = section.querySelector<HTMLAnchorElement>('[data-pdf-download]')
  if (download) {
    download.href = url
    download.setAttribute('download', filename)
  }

  section.querySelectorAll<HTMLButtonElement>('[data-pdf-thumb]').forEach((btn) => {
    const isActive = btn.dataset.pdfUrl === url
    btn.classList.toggle('pdf-header__thumb--active', isActive)
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false')
  })
}

function bindThumbs(section: HTMLElement) {
  section.querySelectorAll<HTMLButtonElement>('[data-pdf-thumb]').forEach((btn) => {
    if (btn.dataset.pdfThumbBound) return
    btn.dataset.pdfThumbBound = 'true'

    btn.addEventListener('click', () => {
      const url = btn.dataset.pdfUrl ?? ''
      const filename = btn.dataset.pdfFilename ?? ''
      setActivePdf(section, url, filename)
    })
  })
}

/**
 * Wires the carousel arrows to scroll the thumbnail strip by one tile per
 * click. The strip itself is a native horizontal scroller; arrows just
 * delegate to `scrollBy`. The nav group is hidden when nothing overflows.
 */
function bindNav(section: HTMLElement) {
  const carousel = section.querySelector<HTMLElement>('[data-pdf-carousel]')
  const thumbs = section.querySelector<HTMLElement>('[data-pdf-viewport]')
  const track = section.querySelector<HTMLElement>('[data-pdf-track]')
  const navGroup = section.querySelector<HTMLElement>('[data-pdf-nav-group]')
  if (!carousel || !thumbs || !track) return
  if (carousel.dataset.pdfNavBound) return
  carousel.dataset.pdfNavBound = 'true'

  const prevBtn = carousel.querySelector<HTMLButtonElement>('[data-pdf-nav="prev"]')
  const nextBtn = carousel.querySelector<HTMLButtonElement>('[data-pdf-nav="next"]')

  const updateNavVisibility = () => {
    const maxScroll = track.scrollWidth - thumbs.clientWidth
    const overflows = maxScroll > 1
    if (navGroup) navGroup.hidden = !overflows
    if (!overflows) return
    const scrollLeft = thumbs.scrollLeft
    if (prevBtn) prevBtn.hidden = scrollLeft <= 1
    if (nextBtn) nextBtn.hidden = scrollLeft >= maxScroll - 1
  }

  const stride = (): number => {
    const items = track.querySelectorAll<HTMLElement>('.pdf-header__thumbs__item')
    if (items.length >= 2) return items[1].offsetLeft - items[0].offsetLeft
    return items[0]?.offsetWidth ?? thumbs.clientWidth
  }

  carousel.querySelectorAll<HTMLButtonElement>('[data-pdf-nav]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const dir = btn.dataset.pdfNav === 'prev' ? -1 : 1
      thumbs.scrollBy({ left: stride() * dir, behavior: 'smooth' })
    })
  })

  updateNavVisibility()
  thumbs.addEventListener('scroll', updateNavVisibility, { passive: true })
  window.addEventListener('resize', updateNavVisibility)
}

function bindPrint(section: HTMLElement) {
  section.querySelectorAll<HTMLButtonElement>('[data-pdf-print]').forEach((trigger) => {
    if (trigger.dataset.pdfPrintBound) return
    trigger.dataset.pdfPrintBound = 'true'

    trigger.addEventListener('click', (event) => {
      event.preventDefault()
      const url = section.dataset.pdfActiveUrl
      if (!url) {
        window.print()
        return
      }

      const frame = section.querySelector<HTMLIFrameElement>('[data-pdf-print-frame]')
      if (!frame) {
        window.open(url, '_blank', 'noopener')
        return
      }

      let triggered = false
      const triggerPrint = () => {
        if (triggered) return
        triggered = true
        try {
          const win = frame.contentWindow
          if (!win) throw new Error('no contentWindow')
          win.focus()
          win.print()
        } catch {
          window.open(url, '_blank', 'noopener')
        }
      }

      frame.onload = triggerPrint
      window.setTimeout(() => {
        if (!triggered) {
          triggered = true
          window.open(url, '_blank', 'noopener')
        }
      }, 4000)

      frame.src = url
    })
  })
}

async function renderInitialCanvases(section: HTMLElement) {
  const canvases = section.querySelectorAll<HTMLCanvasElement>('canvas[data-pdf-canvas]')
  for (const canvas of canvases) {
    if (renderedCanvases.has(canvas)) continue
    const url = canvas.dataset.pdfUrl
    if (!url) continue
    try {
      await renderPdfTo(canvas, url)
    } catch (err) {
      console.warn('[pdf-header] failed to render', url, err)
    }
  }
}

let documentBound = false

export function initPdfHeader(scope: ParentNode = document) {
  const sections = scope.querySelectorAll<HTMLElement>('[data-pdf-header]')
  sections.forEach((section) => {
    bindShare(section)
    bindThumbs(section)
    bindNav(section)
    bindPrint(section)
    void renderInitialCanvases(section)
  })

  if (documentBound) return
  documentBound = true

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null
    if (!target?.closest('[data-pdf-share]')) closeAllShareMenus()
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllShareMenus()
  })
}
